import React from 'react';
import ConfirmModal from 'components/UI/Modal';
import { useAuth } from '../../../lib/Auth';
import { deleteDraftProducts } from '../../../requests/product';
import FlexboxContainer from '../../UI/FlexboxContainer';
import { renderToast, toast } from '../../UI/ToastNotification';
import { useAPI } from '../../../lib/API';
import * as Prod from 'data/Product';
import { navigate } from 'hooks/useHookRouterCompat';
import ProductLocalization from '../ProductLocalization';
import { PRODUCT_STATUS, PRODUCT_TYPE, VALIDATION_STATUS } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { Main } from 'styles/themeColors';

interface Props {
  showDeleteModal: boolean;
  onDelete: Function;
  selectedRows: any[];
  productId?: number;

  showUpdateModal: 'none' | 'update' | 'revise';
  onUpdate: Function;

  showPublishModal: boolean;
  setShowPublishModal: Function;
  setSelectedRows: Function;
  setReload: Function;
  productType?: string;
}

const ProductListingActions = ({
  showDeleteModal,
  onDelete,
  selectedRows,
  showUpdateModal,
  onUpdate,
  productId,
  showPublishModal,
  setShowPublishModal,
  setSelectedRows,
  setReload,
  productType
}: Props) => {
  const { token } = useAuth();
  const typeConfig = getProductTypeConfig(productType);
  const isSubscription = productType === PRODUCT_TYPE.SUBSCRIPTION;
  const getDetailRoute = (id: number) => `${typeConfig.detailUrl}${id}`;

  const onConfirmMessageClick = () => {
    if (showUpdateModal === 'update') {
      prodCloneReq.run(productId);
    } else if (showUpdateModal === 'revise') {
      prodUpdateReq.run([productId], PRODUCT_STATUS.DRAFT);
    }
  };

  const prodCloneReq = useAPI({
    deferFn: Prod.cloneProduct,
    onResolve: result => {
      renderToast(toast.TYPE.SUCCESS, isSubscription ? ProductLocalization.success.cloneSubscription : ProductLocalization.success.clone);
      navigate(getDetailRoute(result.id));
    },
    onReject: (err: Error & { code?: number }) => {
      if (err.code && err.code === 2.9) {
        renderToast(toast.TYPE.ERROR, isSubscription ? ProductLocalization.errors.cloneSubscription : ProductLocalization.errors.clone);
      } else {
        renderToast(toast.TYPE.ERROR, err.message);
      }
      onUpdate('none');
    }
  });

  const prodUpdateReq = useAPI({
    deferFn: Prod.updateProductsStatus,
    onResolve: result => {
      // The API drops products whose business unit has been deactivated, so a
      // resolved request does not on its own mean the product moved.
      if (result.businessUnitInvalidProductIds?.length > 0) {
        renderToast(toast.TYPE.ERROR, isSubscription ? ProductLocalization.errors.businessUnitDeactivatedSubscription : ProductLocalization.errors.businessUnitDeactivated);
        return;
      }

      renderToast(toast.TYPE.SUCCESS, isSubscription ? ProductLocalization.success.statusUpdateSubscription : ProductLocalization.success.statusUpdate);
      navigate(getDetailRoute(result.ids[0]));
    },
    onReject: err => {
      onUpdate('none');
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });


  const prodPublishedReq = useAPI({
    deferFn: Prod.updateProductsStatus,
    onResolve: result => {
      let type = toast.TYPE.SUCCESS;
      let message = isSubscription ? ProductLocalization.success.statusUpdateSubscription : ProductLocalization.success.statusUpdate;
      const hasBadgeIssue = result.badgeInvalidProductIds?.length > 0;
      const hasBusinessUnitIssue = result.businessUnitInvalidProductIds?.length > 0;

      if (result.status === VALIDATION_STATUS.PARTIAL) {
        type = toast.TYPE.WARNING;
        if (hasBusinessUnitIssue) {
          message = isSubscription ? ProductLocalization.warning.businessUnitDeactivatedSubscription : ProductLocalization.warning.businessUnitDeactivated;
        } else if (hasBadgeIssue) {
          message = isSubscription ? ProductLocalization.warning.badgeDeactivatedSubscription : ProductLocalization.warning.badgeDeactivated;
        } else {
          message = isSubscription ? ProductLocalization.warning.materialApiSubscription : ProductLocalization.warning.materialApi;
        }
      } else if (result.status === VALIDATION_STATUS.FAILED) {
        type = toast.TYPE.ERROR;
        if (hasBusinessUnitIssue) {
          message = isSubscription ? ProductLocalization.errors.businessUnitDeactivatedSubscription : ProductLocalization.errors.businessUnitDeactivated;
        } else if (hasBadgeIssue) {
          message = isSubscription ? ProductLocalization.errors.badgeDeactivatedSubscription : ProductLocalization.errors.badgeDeactivated;
        } else {
          message = isSubscription ? ProductLocalization.errors.materialApiSubscription : ProductLocalization.errors.materialApi;
        }
      }

      renderToast(type, message);
      setSelectedRows([]);
      setShowPublishModal(false);
      setReload(true);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
      setSelectedRows([]);
      setShowPublishModal(false);
    }
  });


  const handleProductPublish = () => {
    const selectedIds = selectedRows.map((row) => row.id)
    prodPublishedReq.run(selectedIds, PRODUCT_STATUS.PUBLISHED);
  }

  const entityLabel = isSubscription ? 'subscription' : 'product';
  const entityLabelPlural = isSubscription ? 'subscriptions' : 'products';

  const publishModal = (
    <ConfirmModal
      showModal={showPublishModal}
      onClose={() => setShowPublishModal(false)}
      onClick={handleProductPublish}
      title={`Publish ${selectedRows.length > 1 ? entityLabelPlural : entityLabel}`}
      ghostButtonText="Cancel"
      primaryButtonColor={Main.brandDark}
      borderColor={Main.lightGray}
      color={Main.gray}
      disabled={prodPublishedReq.isLoading}
    >
      <FlexboxContainer flexDirection="column">
        <span>
          {`Are you sure you want to publish ${selectedRows.length > 1 ? `selected ${entityLabelPlural}?` : `this ${entityLabel}?`}`}
        </span>
      </FlexboxContainer>
    </ConfirmModal>
  )

  return (
    <>
      <ConfirmModal
        showModal={showDeleteModal}
        onClose={() => onDelete(false)}
        onClick={() => {
          deleteDraftProducts(
            selectedRows.map((row) => row.id),
            token as string
          )
            .then(() => {
              onDelete(true);
              renderToast(
                toast.TYPE.SUCCESS,
                isSubscription ? ProductLocalization.success.deleteSubscription : ProductLocalization.success.delete
              );
            })
            .catch((err) => {
              renderToast(toast.TYPE.ERROR, err.message);
              onDelete(false);
            });
        }}
        title={`Delete ${selectedRows.length > 1 ? entityLabelPlural : entityLabel}`}
        ghostButtonText="Back"
      >
        <FlexboxContainer flexDirection="column">
          <span>
            {`Are you sure you want to delete ${selectedRows.length > 1
              ? `${selectedRows.length} ${entityLabelPlural}?`
              : `this ${entityLabel}?`
              }`}
          </span>
        </FlexboxContainer>
      </ConfirmModal>

      <ConfirmModal
        showModal={showUpdateModal !== 'none'}
        onClose={() => onUpdate('none')}
        onClick={onConfirmMessageClick}
        title={`Update ${entityLabel}`}
        ghostButtonText="Back"
        disabled={prodCloneReq.isLoading || prodUpdateReq.isLoading}
      >
        <FlexboxContainer flexDirection="column">
          <span>
            {showUpdateModal === "update" && (isSubscription ? ProductLocalization.warning.updateSubscription : ProductLocalization.warning.update)}
            {showUpdateModal === "revise" && (isSubscription ? ProductLocalization.warning.reviseSubscription : ProductLocalization.warning.revise)}
          </span>
        </FlexboxContainer>
      </ConfirmModal>

      {publishModal}
    </>
  );
};

export default ProductListingActions;
